/**
 * PromoPopup
 *
 * Shows a one-time popup of the highest-priority currently-active promotion
 * the moment a logged-in user lands on the site. Dismissal or "Book Now" both
 * mark the promo as seen on the server so the user never sees it again.
 *
 * Anonymous visitors are skipped — we don't want a popup blocking the signup
 * flow or login screen.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { X, Tag, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const SKIP_PATHS = ['/login', '/signup', '/forgot-password', '/reset-password'];

const formatDiscount = (promo) => {
  if (!promo) return '';
  if (promo.discount_type === 'percentage') return `${promo.discount_value}% OFF`;
  if (promo.discount_type === 'fixed') return `$${promo.discount_value} OFF`;
  return promo.title;
};

const PromoPopup = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [promo, setPromo] = useState(null);
  const [closing, setClosing] = useState(false);
  const [marking, setMarking] = useState(false);

  const fetchPopup = useCallback(async () => {
    if (!currentUser) {
      console.debug('[PromoPopup] skipped — no current user');
      return;
    }
    if (SKIP_PATHS.some(p => location.pathname.startsWith(p))) {
      console.debug('[PromoPopup] skipped — on auth page', location.pathname);
      return;
    }
    // Staff don't need promo popups
    if (currentUser.role === 'admin' || currentUser.role === 'barber') {
      console.debug('[PromoPopup] skipped — staff role:', currentUser.role);
      return;
    }

    try {
      const res = await api.get('/api/promotions/popup');
      console.debug('[PromoPopup] response:', res.data);
      if (res.data?.promotion) {
        setPromo(res.data.promotion);
      }
    } catch (e) {
      console.warn('[PromoPopup] fetch error', e?.response?.status, e?.response?.data);
    }
  }, [currentUser, location.pathname]);

  // Run on login / route change
  useEffect(() => { fetchPopup(); }, [fetchPopup]);

  const markSeen = useCallback(async () => {
    if (!promo || marking) return;
    setMarking(true);
    try {
      await api.post(`/api/promotions/${promo.id}/seen`);
    } catch (e) {
      // Non-blocking — user already saw it; worst case it reappears next time
      console.debug('Mark seen failed', e?.response?.status);
    } finally {
      setMarking(false);
    }
  }, [promo, marking]);

  const handleClose = async () => {
    setClosing(true);
    await markSeen();
    setPromo(null);
    setClosing(false);
  };

  const handleBookNow = async () => {
    setClosing(true);
    await markSeen();
    const code = promo?.promo_code || '';
    setPromo(null);
    setClosing(false);
    navigate(code ? `/booking?promo=${encodeURIComponent(code)}` : '/booking');
  };

  if (!promo) return null;

  const discount = formatDiscount(promo);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={handleClose}
      data-testid="promo-popup-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-gradient-to-br from-purple-950 via-zinc-950 to-purple-950 border-2 border-lime-400/40 rounded-2xl shadow-2xl shadow-lime-500/20 overflow-hidden animate-in zoom-in-95 duration-300"
        data-testid="promo-popup"
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={closing}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-zinc-300 hover:text-white transition"
          aria-label="Close"
          data-testid="promo-popup-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Banner image if available */}
        {promo.image_url && (
          <div className="relative aspect-[16/9] bg-zinc-900">
            <img
              src={promo.image_url}
              alt={promo.title}
              className="w-full h-full object-cover"
              data-testid="promo-popup-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-purple-950 via-purple-950/40 to-transparent" />
          </div>
        )}

        <div className="p-6 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-3 rounded-full bg-lime-400/15 border border-lime-400/40 text-lime-300 text-xs font-semibold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" /> Limited time
          </div>

          <h2 className="text-3xl font-bold text-white mb-2 leading-tight" data-testid="promo-popup-title">
            {promo.title}
          </h2>

          {discount && (
            <p className="text-4xl font-extrabold text-lime-400 mb-3 tracking-tight" data-testid="promo-popup-discount">
              {discount}
            </p>
          )}

          {promo.description && (
            <p className="text-zinc-300 text-sm mb-4 leading-relaxed">
              {promo.description}
            </p>
          )}

          {promo.promo_code && (
            <div className="flex items-center justify-center gap-2 mb-5">
              <Tag className="w-4 h-4 text-purple-300" />
              <span className="text-zinc-400 text-xs uppercase tracking-wider">Code:</span>
              <span
                className="font-mono font-bold text-purple-200 bg-purple-500/15 border border-purple-400/30 px-3 py-1 rounded select-all"
                data-testid="promo-popup-code"
              >
                {promo.promo_code}
              </span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={closing}
              className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
              data-testid="promo-popup-maybe-later-btn"
            >
              Maybe later
            </Button>
            <Button
              onClick={handleBookNow}
              disabled={closing}
              className="flex-1 bg-lime-400 text-black hover:bg-lime-500 font-bold"
              data-testid="promo-popup-book-now-btn"
            >
              Book now →
            </Button>
          </div>

          {promo.valid_until && (
            <p className="text-zinc-500 text-[11px] mt-4">
              Offer valid through {new Date(promo.valid_until + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromoPopup;
